import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { TableCell, IconButton, Tooltip } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { getContrastColor } from '#component/theme';
import DeviceEvent from '#component/device/dashboard/event';
import DeviceCharge from '#component/device/dashboard/charge';
import DeviceTemperature from '#component/device/dashboard/temperature';
import DeviceIcon from '#component/icon/device-icon';
import HighlightWord from '#component/general/highlight-word';
import DownloadDeviceLabel from '#component/device/dashboard/download-device-label';
import { getYoungestMetrics, isActiveDevice } from '#component/device/helper';
import { deviceTypesQR } from '#component/device/dashboard/helper';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';

const TitleContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center'
}));

const StyledDeviceIcon = styled(DeviceIcon)(({ theme }) => ({
  marginRight: theme.spacing(1)
}));

export const DeviceTitleCell = ({ data, meta }) => {
  const isActive = isActiveDevice(data);
  /* TODO: remove freezer special-case once
   * api/meta/device (Device API /devicetype)
   * returns icon: 'freezer' (currently returns 'thirdparty')
   */
  const icon = data.type === 'freezer' ?
    'freezer' :
    meta.device?.find(m => m.name === data.type)?.icon;

  const filter = useSelector(s => s.listFilter, shallowEqual) ?? {};
  const highlightedSearchResult = getHighlightedSearchResult(filter);

  return (
    <TableCell
      scope="row"
      style={{
        whiteSpace: 'nowrap',
        width: 'auto',
        height: 57
      }}
    >
      <TitleContainer>
        <StyledDeviceIcon
          icon={icon}
          isActive={isActive}
        />
        {highlightedSearchResult ? <HighlightWord
          textToHighlight={data.title || data.uid}
          searchWord={highlightedSearchResult}
        /> : data.title || data.uid}
      </TitleContainer>
    </TableCell>
  );
};

export const DeviceChargeCell = ({ data, account }) => {
  const [battery] = getYoungestMetrics(data?.lastMetric?.youngest, account, ['battery'], true);

  return (
    <TableCell align="left">
      <DeviceCharge
        device={data}
        account={account}
        battery={battery}
      />
    </TableCell>
  );
};

export const DeviceTemperatureCell = ({ data, account }) => {
  const [temperature] = getYoungestMetrics(data?.lastMetric?.youngest, account, ['temperature'], true);

  return (
    <TableCell align="left">
      <DeviceTemperature
        device={data}
        account={account}
        temperature={temperature}
      />
    </TableCell>
  );
};

export const DeviceEventCell = ({ data, loading, account }) => (
  <TableCell align="left">
    <DeviceEvent
      device={data}
      account={account}
      loading={loading}
    />
  </TableCell>
);

const ProjectsTreeIcon = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(0.75)
}));

export const DeviceDetailsCell = ({
  data,
  theme,
  account,
  setDetailsDialog,
  setSelectedDevice
}) => {
  const isActive = isActiveDevice(data);

  const onClick = e => {
    e.stopPropagation();
    setDetailsDialog(true);
    setSelectedDevice(data);
  };

  return account?.userType < 10 && (
    <TableCell align="left">
      <ProjectsTreeIcon
        size="small"
        aria-label="Details"
        color="inherit"
        onClick={onClick}
      >
        <Tooltip title="Details">
          <AccountTreeOutlinedIcon
            style={{
              color: `${
                isActive ? getContrastColor() : theme.palette.grey[600]
              }`
            }}
          />
        </Tooltip>
      </ProjectsTreeIcon>
    </TableCell>
  );
};

export const DeviceQRCell = ({ data }) => (
  <TableCell align="left">
    {deviceTypesQR.includes(data.type) && <DownloadDeviceLabel device={data} /> }
  </TableCell>
);
